/**
 * Calculate duration between two dates in minutes
 * @param {Date} start - Start time
 * @param {Date} end - End time
 * @returns {number} Duration in minutes
 */
export function calculateDuration(start, end) {
  if (!start || !end || !(start instanceof Date) || !(end instanceof Date)) {
    return 0;
  }
  return Math.round((end - start) / (1000 * 60));
}

/**
 * Check if a time slot is available (not overlapping with existing bookings)
 * @param {Date} start - Proposed start time
 * @param {Date} end - Proposed end time
 * @param {Array} existingBookings - Array of existing bookings to check against
 * @param {string} excludeBookingId - Booking ID to exclude from check (for updates)
 * @returns {boolean} True if time slot is available
 */
export function isTimeSlotAvailable(start, end, existingBookings = [], excludeBookingId = null) {
  if (!start || !end || !(start instanceof Date) || !(end instanceof Date)) {
    return false;
  }

  if (start >= end) {
    return false;
  }

  for (const booking of existingBookings) {
    // Skip cancelled bookings and excluded booking
    if (booking.status === 'cancelled' || booking._id === excludeBookingId) {
      continue;
    }

    const bookingStart = booking.start;
    const bookingEnd = booking.end;

    // Check for time overlap
    if (start < bookingEnd && end > bookingStart) {
      return false;
    }
  }

  return true;
}

/**
 * Format date and time for display
 * @param {Date} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export function formatDateTime(date, options = {}) {
  if (!date || !(date instanceof Date)) {
    return '';
  }

  const defaults = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  };

  const formatOptions = { ...defaults, ...options };
  return date.toLocaleString('en-GB', formatOptions);
}

/**
 * Check if two time periods overlap
 * @param {Date} start1 - Start of first period
 * @param {Date} end1 - End of first period  
 * @param {Date} start2 - Start of second period
 * @param {Date} end2 - End of second period
 * @returns {boolean} True if periods overlap
 */
export function timePeriodsOverlap(start1, end1, start2, end2) {
  return start1 < end2 && end1 > start2;
}

/**
 * Get the next available time slot after a given time
 * @param {Date} afterTime - Time to search after
 * @param {number} duration - Duration in minutes
 * @param {Array} existingBookings - Existing bookings to avoid
 * @param {Object} workingHours - Working hours constraints
 * @returns {Object|null} Next available slot or null
 */
export function getNextAvailableSlot(afterTime, duration, existingBookings = [], workingHours = null) {
  if (!afterTime || !duration || duration <= 0) {
    return null;
  }

  const proposedStart = new Date(afterTime);
  const proposedEnd = new Date(proposedStart.getTime() + (duration * 60 * 1000));

  // Apply working hours if provided
  if (workingHours) {
    const startHour = proposedStart.getHours();
    const endHour = proposedEnd.getHours();
    
    if (startHour < workingHours.start || endHour > workingHours.end) {
      // Adjust to next available working hour
      const nextDay = new Date(proposedStart);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(workingHours.start, 0, 0, 0);
      return getNextAvailableSlot(nextDay, duration, existingBookings, workingHours);
    }
  }

  if (isTimeSlotAvailable(proposedStart, proposedEnd, existingBookings)) {
    return {
      start: proposedStart,
      end: proposedEnd
    };
  }

  // Find next gap in bookings
  const futureBookings = existingBookings
    .filter(booking => booking.start > afterTime && booking.status !== 'cancelled')
    .sort((a, b) => a.start - b.start);

  if (futureBookings.length === 0) {
    return {
      start: proposedStart,
      end: proposedEnd
    };
  }

  // Try after each booking
  for (const booking of futureBookings) {
    const nextSlot = getNextAvailableSlot(booking.end, duration, existingBookings, workingHours);
    if (nextSlot) {
      return nextSlot;
    }
  }

  return null;
} 